/**
 * Trader portal shell. All the chrome lives in PortalShell (the reference
 * app's layout); this file only supplies the guard, the tenant-filtered nav
 * and the KYC pill.
 */
import { Navigate, useLocation } from "react-router-dom";
import { useMe } from "@/hooks/useAuth";
import { useFilteredTraderNav } from "@/hooks/useEnabledNav";
import { Skeleton } from "@/components/ui/Skeleton";
import { cn } from "@/lib/utils";
import { PortalShell } from "./PortalShell";
import { RDFX_MAIN_NAV, RDFX_SECTIONS, RDFX_PAGE_LABELS, type RdfxNavSection } from "./rdfxNav";
import { useCurrentTenant } from "@/hooks/useTenant";
import { Send } from "lucide-react";

export function TraderShell() {
  const { data: me, isPending, isError } = useMe();
  const location = useLocation();
  const traderNav = useFilteredTraderNav();
  const { data: tenantInfo } = useCurrentTenant();

  // The broker's Telegram group, if they've set one. It is not a route, so it
  // rides along as an external entry rather than going in the static nav.
  const telegramUrl = tenantInfo?.tenant?.settings?.trader?.telegramGroupUrl?.trim();
  const sections: RdfxNavSection[] = telegramUrl
    ? [...RDFX_SECTIONS, {
        title: "Community",
        items: [{ to: "telegram-group", label: "Join Telegram Group", icon: Send, href: telegramUrl }],
      }]
    : RDFX_SECTIONS;

  if (isPending) {
    return (
      <div className="theme-rdfx flex min-h-screen bg-background">
        <div className="hidden w-64 space-y-2 border-r border-border p-4 lg:block">
          <Skeleton className="h-9 w-32" />
          {Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-9 w-full" />)}
        </div>
        <div className="flex-1 space-y-3 p-8">
          <Skeleton className="h-16 w-full" />
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-28 w-full" />)}
          </div>
        </div>
      </div>
    );
  }

  if (isError || !me) return <Navigate to="/login" replace state={{ from: location.pathname }} />;

  if (me.role !== "trader" && me.role !== "ib" && me.role !== "affiliate") {
    if (me.role === "platform_admin") return <Navigate to="/platform/dashboard" replace />;
    if (me.role === "admin") return <Navigate to="/admin/dashboard" replace />;
    return <Navigate to="/login" replace />;
  }

  const allowed = new Set(traderNav.map((i) => i.to));
  const isIb = me.role === "ib" || Boolean(me.isIb);
  const verified = me.kycStatus === "verified";

  return (
    <PortalShell
      mainNav={RDFX_MAIN_NAV}
      sections={sections}
      allowed={allowed}
      pageLabels={RDFX_PAGE_LABELS}
      name={me.fullName ?? "Trader"}
      roleLabel={isIb ? "IB Partner" : "Live Trader"}
      profileHref="/trader/profile"
      // Once a trader is an IB, the entry point leads to the partner area.
      resolveItem={(i) => (isIb && i.to === "/trader/become-ib" ? { ...i, to: "/ib/dashboard" } : i)}
      statusPill={
        <div className={cn(
          "hidden items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold md:flex",
          verified ? "border-success/40 text-success" : "border-warning/40 text-warning",
        )}>
          <div className="h-1.5 w-1.5 rounded-full bg-current" />
          {verified ? "KYC Verified" : "KYC Pending"}
        </div>
      }
    />
  );
}
